import {
  Button,
  ButtonVariant,
  FormMessage,
  IconIdentifier,
  Input,
  InputGroup,
  Placement,
  ToggleSwitch,
  Tooltip,
} from '@aus-platform/design-system';
import { useAppSelector } from 'app/hooks';
import {
  selectMap3DState,
  selectMap3dSidebar,
} from 'map-3d/shared/map-3d-slices';
import { useEffect, useState } from 'react';
import { Placeholders } from 'shared/constants';
import { Formatter } from 'shared/helpers';
import { MarkArea, MarkAreaDrawingToolType } from './enums';
import type { MarkAreaToggleProps } from './types';

export const MarkAreaToggle: React.FC<MarkAreaToggleProps> = ({
  currentMarkedArea,
  onToggleChange,
  drawingHandlers,
  isDrawingToolEnabled,
  workspaceLayerError,
  markAreaError,
  selectLayerSelectProps,
  headerComponent,
  typeOfLayerComponent,
  drawingType = MarkAreaDrawingToolType.Polygon,
  isRequired = false,
  isDisabled = false,
  buttonsTooltipProps,
}) => {
  // Selectors.
  const { cesiumProxy } = useAppSelector(selectMap3DState);
  const { isOverlayOpen } = useAppSelector(selectMap3dSidebar);

  const [isButtonsHovered, setIsButtonsHovered] = useState(false);

  const shouldShowTooltipWhenDisabled =
    !!buttonsTooltipProps &&
    isDisabled &&
    (buttonsTooltipProps.showWhenDisabled ?? false);

  // useEffects.
  useEffect(() => {
    if (isOverlayOpen && isDrawingToolEnabled) {
      drawingHandlers.endDrawingHandler();
    }
  }, [isOverlayOpen]);

  useEffect(() => {
    // TODO: check why cesium is not rendering.
    const handleMouseMove = () => {
      cesiumProxy?.viewer.scene.requestRender();
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Variables.
  const errorMessage =
    currentMarkedArea === MarkArea.Draw
      ? markAreaError
      : workspaceLayerError || markAreaError;

  return (
    <div
      onMouseEnter={() =>
        shouldShowTooltipWhenDisabled && setIsButtonsHovered(true)
      }
      onMouseLeave={() =>
        shouldShowTooltipWhenDisabled && setIsButtonsHovered(false)
      }
    >
      <InputGroup className="mark-area">
        {headerComponent ?? (
          <Input.Label className="mark-area__heading" isRequired={isRequired}>
            Mark Area
          </Input.Label>
        )}
        <ToggleSwitch
          leftTitle={Formatter.toTitleCase(MarkArea.Draw, '_')}
          rightTitle={Formatter.toTitleCase(MarkArea.LayerFromWorkspace, '_')}
          leftSelected={currentMarkedArea === MarkArea.Draw}
          onChange={onToggleChange}
          disabled={isDisabled}
        />
        {currentMarkedArea === MarkArea.Draw ? (
          <div className="mark-area__btn">
            {isDrawingToolEnabled ? (
              <>
                <Button
                  className="mark-area__btn--exit-draw-polygon"
                  variant={ButtonVariant.Primary}
                  leftIconIdentifier={
                    drawingType == MarkAreaDrawingToolType.Polygon
                      ? IconIdentifier.Polygon
                      : IconIdentifier.Line
                  }
                  onClick={drawingHandlers.endDrawingHandler}
                  disabled={isDisabled}
                >
                  Exit Drawing Mode
                </Button>
                <Button
                  variant={ButtonVariant.Outline}
                  leftIconIdentifier={IconIdentifier.Reset}
                  onClick={drawingHandlers.resetDrawingHandler}
                  className="mark-area__btn--redo"
                  disabled={isDisabled}
                >
                  Reset Drawing
                </Button>
              </>
            ) : (
              <>
                <Tooltip
                  hoverText={buttonsTooltipProps?.text}
                  placement={buttonsTooltipProps?.placement ?? Placement.Top}
                  className={buttonsTooltipProps?.className}
                  show={shouldShowTooltipWhenDisabled && isButtonsHovered}
                >
                  <Button
                    className="mark-area__btn--draw-polygon"
                    variant={ButtonVariant.Outline}
                    leftIconIdentifier={
                      drawingType == MarkAreaDrawingToolType.Polygon
                        ? IconIdentifier.Polygon
                        : IconIdentifier.Line
                    }
                    onClick={drawingHandlers.startDrawingHandler}
                    disabled={isDrawingToolEnabled || isDisabled}
                  >
                    Enter Drawing Mode
                  </Button>
                </Tooltip>
              </>
            )}
          </div>
        ) : (
          <>
            {typeOfLayerComponent}
            <InputGroup className="mark-area__select-layer">
              <Input.Label isRequired={isRequired}>Select Layer</Input.Label>
              <Input.Select
                placeholder={Placeholders.SelectLayerFromWorkspace}
                {...selectLayerSelectProps}
              />
            </InputGroup>
          </>
        )}

        {errorMessage && (
          <FormMessage
            className="mark-area__form-message"
            message={errorMessage}
          />
        )}
      </InputGroup>
    </div>
  );
};
